import { useState, useCallback } from 'react';
import { useAppStore } from '../store';
import { useProjectStore } from '../store/projectStore';
import { saveProjectHandle } from '../utils/handle-storage';
import {
  setProjectRoot,
  createDirectory,
  writeFile,
  addToRecentProjects,
} from '../services/FileSystemService';
import { ProjectStateManager } from '../services/ProjectStateManager';
import { refreshProjectFiles } from '../utils/project-files';
import type { ProjectConfig, ProjectManifest } from '../../core/types/project.types';

interface UseProjectCreationReturn {
  isCreating: boolean;
  error: string | null;
  parentHandle: FileSystemDirectoryHandle | null;
  setParentHandle: (handle: FileSystemDirectoryHandle | null) => void;
  createProject: (config: ProjectConfig) => Promise<string | null>;
  clearError: () => void;
}

export function useProjectCreation(): UseProjectCreationReturn {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parentHandle, setParentHandle] = useState<FileSystemDirectoryHandle | null>(null);

  const { setCurrentView, closeNewProjectDialog } = useAppStore();
  const { setCurrentProject, addRecentProject } = useProjectStore();

  const createProject = useCallback(async (config: ProjectConfig): Promise<string | null> => {
    // Check if parentHandle is set
    if (!parentHandle) {
      setError('No location selected. Please select a folder first.');
      return null;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Generate project folder name from project name (slug)
      const folderName = config.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

      // Check if folder already exists
      try {
        await parentHandle.getDirectoryHandle(folderName);
        // If we get here, folder exists
        setError(`A folder named "${folderName}" already exists in this location.`);
        setIsCreating(false);
        return null;
      } catch {
        // Good - folder doesn't exist, we can create it
      }

      // Create project folder
      const projectHandle = await parentHandle.getDirectoryHandle(folderName, { create: true });

      // Set as active project root using the exported function
      setProjectRoot(projectHandle);

      // Create folder structure
      const folders = [
        'assets',
        'assets/sprites',
        'assets/audio',
        'assets/fonts',
        'assets/scenes',
        'assets/scripts',
        'assets/prefabs',
        '.editor-state',
      ];

      for (const folder of folders) {
        await createDirectory(folder, true);
      }

      // Create project manifest
      const manifest: ProjectManifest = {
        name: config.name,
        version: '1.0.0',
        engine: 'pixijs-editor',
        engineVersion: '1.0.0',
        scriptLanguage: config.scriptLanguage,
        resolution: config.resolution,
        template: config.template,
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
        scenes: ['assets/scenes/main.scene'],
        startScene: 'assets/scenes/main.scene',
      };

      await writeFile('.project.json', JSON.stringify(manifest, null, 2));

      // Create default scene based on template
      const defaultScene = createDefaultScene(config.template);
      await writeFile(
        'assets/scenes/main.scene',
        JSON.stringify(defaultScene, null, 2)
      );

      // Create default editor state files
      const defaultEditorState = {
        version: '1.0.0',
        lastModified: new Date().toISOString(),
        sceneView: {
          panX: 0,
          panY: 0,
          zoom: 1,
          activeTool: 'select',
        },
        projectPanel: {
          expandedFolders: ['assets'],
          selectedAssetPath: null,
        },
        livePreview: {
          devicePreset: null,
          customWidth: config.resolution.width,
          customHeight: config.resolution.height,
          scale: 1,
        },
        console: {
          commandHistory: [],
        },
      };

      await writeFile(
        '.editor-state/editor-state.json',
        JSON.stringify(defaultEditorState, null, 2)
      );

      // Create empty scene state
      const sceneState = {
        version: '1.0.0',
        gameObjects: {},
        rootIds: [],
        selectedIds: [],
      };

      await writeFile(
        '.editor-state/scene-state.json',
        JSON.stringify(sceneState, null, 2)
      );

      // Create empty script editor state
      const scriptEditorState = {
        openFiles: [],
        activeTabPath: null,
      };

      await writeFile(
        '.editor-state/script-tabs.json',
        JSON.stringify(scriptEditorState, null, 2)
      );

      // Create empty AI conversations
      const aiConversations = {
        conversations: [],
        activeConversationId: null,
      };

      await writeFile(
        '.editor-state/ai-conversations.json',
        JSON.stringify(aiConversations, null, 2)
      );

      // Generate project ID and save handle
      const projectId = `${parentHandle.name}/${folderName}`;
      await saveProjectHandle(projectId, projectHandle);

      // Create recent project entry
      const recentProject = {
        name: config.name,
        path: projectId,
        size: 0,
        lastOpened: new Date().toISOString(),
      };

      // Add to recent projects - save to IndexedDB and localStorage
      await addToRecentProjects(recentProject, projectHandle);

      // Also update the store
      addRecentProject(recentProject);

      // If openAfterCreate is enabled, open the project
      if (config.openAfterCreate) {
        setCurrentProject(manifest, projectId);
        setCurrentView('editor');

        // Initialize ProjectStateManager so save operations work immediately
        ProjectStateManager.initialize(projectId);

        // Refresh project files to show real folder contents
        try {
          await refreshProjectFiles();
          console.log('[useProjectCreation] Project files refreshed');
        } catch (err) {
          console.warn('[useProjectCreation] Failed to refresh project files:', err);
        }

        // Load the start scene so it shows as a saved file (not "Untitled")
        try {
          const { useSceneStore } = await import('../store/sceneStore');
          await useSceneStore.getState().loadScene(manifest.startScene);
          console.log('[useProjectCreation] Start scene loaded:', manifest.startScene);
        } catch (err) {
          console.warn('[useProjectCreation] Failed to load start scene:', err);
        }

        // Fire-and-forget: slot core install runs in background
        // The SlotCoreInstallToast in EditorLayout handles progress/errors
        window.dispatchEvent(new CustomEvent('slot-core:install-requested'));

      }

      // Close the dialog
      closeNewProjectDialog();

      return projectId;
    } catch (err: unknown) {
      console.error('Project creation failed:', err);

      const errorObj = err as { name?: string; message?: string };
      if (errorObj.name === 'NotAllowedError') {
        setError('Permission denied. Please grant access to the selected folder.');
      } else if (errorObj.name === 'QuotaExceededError') {
        setError('Not enough disk space to create project.');
      } else if (errorObj.name === 'InvalidModificationError') {
        setError('Invalid folder name. Please use only letters, numbers, and hyphens.');
      } else {
        setError(errorObj.message || 'Failed to create project. Please try again.');
      }
      return null;
    } finally {
      setIsCreating(false);
    }
  }, [parentHandle, setCurrentProject, setCurrentView, closeNewProjectDialog, addRecentProject]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    createProject,
    isCreating,
    error,
    parentHandle,
    setParentHandle,
    clearError,
  };
}

// Helper to create default scene content based on template
function createDefaultScene(template: string) {
  const scene = {
    name: 'Main Scene',
    version: '1.0.0',
    objects: [] as Array<{
      id: string;
      name: string;
      type: string;
      transform: {
        position: { x: number; y: number };
        rotation: number;
        scale: { x: number; y: number };
        anchor: { x: number; y: number };
      };
      components: Array<{
        id: string;
        type: string;
        enabled: boolean;
        data: Record<string, unknown>;
      }>;
      children: string[];
    }>,
    settings: {
      backgroundColor: '#1a1a2e',
    },
  };

  // Empty template starts with no objects
  if (template === 'empty') {
    return scene;
  }

  // Add main camera for other templates
  scene.objects.push({
    id: generateId(),
    name: 'MainCamera',
    type: 'camera',
    transform: {
      position: { x: 0, y: 0 },
      rotation: 0,
      scale: { x: 1, y: 1 },
      anchor: { x: 0.5, y: 0.5 },
    },
    components: [
      {
        id: generateId(),
        type: 'Camera',
        enabled: true,
        data: {
          isMain: true,
          backgroundColor: '#1a1a2e',
          zoom: 1,
        },
      },
    ],
    children: [],
  });

  // Template-specific objects could be added here in the future
  switch (template) {
    case 'platformer':
    case 'topdown':
    case 'puzzle':
    case 'shooter':
    case 'rpg':
    default:
      // Other templates start with a camera
      break;
  }

  return scene;
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 11);
}
